/**
 * <quick-buy> — a compact "quick buy" drawer for product cards.
 *
 * It reuses the theme's existing quick-view drawer machinery (fetch a product
 * view, inject its markup into the drawer, re-run scripts) but loads a leaner
 * buy box that INCLUDES the custom Skio subscription options form.
 *
 * How it works:
 * - Extends the already-registered `quick-view` element (defined in theme.js),
 *   so all of the modal open/close, focus-trap and cart-close behavior is
 *   inherited for free.
 * - Overrides only the fetch target: `?view=quick-buy` renders
 *   templates/product.quick-buy.json (title, price, variant picker,
 *   skio-plan-picker, inventory, buy buttons) instead of `?view=modal`.
 *
 * The injected markup contains `<script type="module" src="skio-plan-picker-component.js">`.
 * Because that module is already registered as a custom element on first use,
 * the injected `<skio-plan-picker>` upgrades automatically from its attributes,
 * so each card's drawer gets its own working subscribe / one-time picker.
 */
(function () {
  function defineQuickBuy() {
    const QuickView = customElements.get('quick-view');

    // Nothing to extend / already defined — bail out safely.
    if (!QuickView || customElements.get('quick-buy')) return;

    class QuickBuy extends QuickView {
      // Fetch the lean, subscription-aware buy box instead of the full modal PDP.
      quickview() {
        const drawerContent = this.querySelector(this.selector);
        const productUrl = this.getAttribute('data-product-url').split('?')[0];
        const sectionUrl = `${productUrl}?view=quick-buy`;

        fetch(sectionUrl)
          .then((response) => response.text())
          .then((responseText) => {
            setTimeout(() => {
              const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
              const productElement = parsedHTML.querySelector(this.selector);

              if (!productElement) return;

              this.setInnerHTML(drawerContent, productElement.innerHTML);
              theme.a11y.trapFocus(this, this.focusElement);

              if (window.Shopify && Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
              }

              document.dispatchEvent(new CustomEvent('quickview:loaded', {
                detail: {
                  productUrl: this.getAttribute('data-product-url')
                }
              }));
            }, 200);
          })
          .catch((e) => {
            console.error(e);
          });
      }
    }

    customElements.define('quick-buy', QuickBuy);
  }

  if (customElements.get('quick-view')) {
    defineQuickBuy();
  } else {
    customElements.whenDefined('quick-view').then(defineQuickBuy);
  }
})();
